import { AlertCircle, BrainCircuit, FileText, FileUp, RefreshCw, Search, Send, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  deleteKnowledgeDocument,
  listKnowledgeDocuments,
  queryKnowledge,
  uploadKnowledgeFile,
  type KnowledgeChunk,
  type KnowledgeDocument,
} from "@/services/knowledge";

export function KnowledgePanel() {
  const [question, setQuestion] = React.useState("");
  const [topK, setTopK] = React.useState(5);
  const [chunks, setChunks] = React.useState<KnowledgeChunk[]>([]);
  const [querying, setQuerying] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [documents, setDocuments] = React.useState<KnowledgeDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadDocuments = React.useCallback(async () => {
    setLoadingDocs(true);
    setError(null);
    try {
      const data = await listKnowledgeDocuments();
      setDocuments(data);
    } catch (err) {
      setDocuments([]);
      setError(extractErrorMessage(err, "无法加载知识库文档列表。"));
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  React.useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const handleQuery = async () => {
    if (!question.trim()) return;
    setQuerying(true);
    setError(null);
    try {
      const data = await queryKnowledge(question, topK);
      setChunks(data.chunks);
    } catch (err) {
      setChunks([]);
      setError(extractErrorMessage(err, "知识库查询失败，请确认 RAG 服务已配置。"));
    } finally {
      setQuerying(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadKnowledgeFile(file);
      await loadDocuments();
    } catch (err) {
      setError(extractErrorMessage(err, "上传文档失败，仅支持 UTF-8 文本文件。"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!window.confirm("确定删除该文档及其所有切片？此操作不可撤销。")) return;
    try {
      await deleteKnowledgeDocument(documentId);
      await loadDocuments();
    } catch (err) {
      setError(extractErrorMessage(err, "删除文档失败。"));
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BrainCircuit className="h-4 w-4" />
          知识库（RAG）
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-hidden">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">RAG 服务异常</p>
              <p className="text-xs opacity-90">{error}</p>
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <FileUp className="h-3.5 w-3.5" />
              上传文档
            </h4>
            <p className="mb-2 text-xs text-muted-foreground">支持 .txt / .md 等 UTF-8 文本文件，上传后自动切片并向量化。</p>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept=".txt,.md,.json,.csv"
                onChange={(e) => void handleFileChange(e)}
                disabled={uploading}
                className="h-9 text-xs"
              />
              {uploading && <span className="text-xs text-muted-foreground">上传中...</span>}
            </div>
          </div>

          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Search className="h-3.5 w-3.5" />
              语义检索
            </h4>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Textarea
                placeholder="输入问题，例如：项目矩阵的定义是什么？"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={2}
                className="flex-1 text-xs"
              />
              <div className="flex flex-col gap-2">
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={topK}
                  onChange={(e) => setTopK(Math.max(1, Math.min(20, Number(e.target.value) || 5)))}
                  className="h-9 w-20 text-xs"
                />
                <Button size="sm" disabled={querying || !question.trim()} onClick={() => void handleQuery()}>
                  <Send className={cn("mr-1 h-3.5 w-3.5", querying && "animate-pulse")} />
                  {querying ? "检索中..." : "检索"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-md border">
          <div className="flex items-center justify-between border-b px-4 py-2 text-xs font-medium">
            <span className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" />
              文档列表
            </span>
            <Button variant="ghost" size="sm" onClick={() => void loadDocuments()} disabled={loadingDocs}>
              <RefreshCw className={cn("h-3 w-3", loadingDocs && "animate-spin")} />
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            {documents.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">暂无文档</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">文件名</TableHead>
                    <TableHead className="text-xs">切片数</TableHead>
                    <TableHead className="text-xs">上传时间</TableHead>
                    <TableHead className="w-16 text-xs">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="max-w-xs truncate text-xs">{doc.name}</TableCell>
                      <TableCell className="text-xs">{doc.chunk_count}</TableCell>
                      <TableCell className="text-xs">
                        {doc.created_at ? new Date(doc.created_at).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => void handleDelete(doc.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {chunks.length > 0 && (
          <div className="flex flex-1 flex-col overflow-hidden rounded-md border">
            <div className="border-b px-4 py-2 text-xs font-medium">检索结果</div>
            <div className="flex-1 overflow-auto p-3">
              <div className="space-y-3">
                {chunks.map((chunk) => (
                  <div key={chunk.id} className="rounded-md border bg-card p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] text-muted-foreground">ID: {chunk.id}</span>
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                        相似度 {(chunk.similarity * 100).toFixed(1)}%
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed">{chunk.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message || fallback;
  }
  return fallback;
}
